import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Link } from 'react-router-dom';
import { BarChart3, Code2, MoreHorizontal, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlowStatusBadge, UnpublishedChangesBadge } from '@/components/vsl-dinamica/shared/FlowStatusBadge';
import type { FlowListItem } from '@/services/dynamicFlowsService';
import { formatNumber, formatPercent, safeRate } from '../metrics/format';
import { formatEditedAgo, formatVideoCount } from './flowListUtils';

interface FlowCardProps {
  flow: FlowListItem;
  busy?: boolean;
  onCopyCode: (flow: FlowListItem) => void;
  onRename: (flow: FlowListItem) => void;
  onPause: (flow: FlowListItem) => void;
  onResume: (flow: FlowListItem) => void;
  onDelete: (flow: FlowListItem) => void;
}

const buttonBase =
  'inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[10px] text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0057FF] focus-visible:ring-offset-2';

export function FlowCard({ flow, busy, onCopyCode, onRename, onPause, onResume, onDelete }: FlowCardProps) {
  useLanguage();
  const editorPath = `/vsl-dinamica/${flow.id}`;
  const metricsPath = `${editorPath}/metricas`;
  const hasMetrics = flow.status !== 'draft';
  const { views, plays } = flow.stats_30d || { views: 0, plays: 0 };

  return (
    <article className="flex flex-col gap-3.5 rounded-2xl border border-[#D9E0EA] bg-white p-[18px]">
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex min-w-0 flex-col gap-2">
          <h2 className="truncate text-base font-semibold text-[#111827]" title={flow.name}>
            {flow.name}
          </h2>
          <div className="flex flex-wrap gap-1.5">
            <FlowStatusBadge status={flow.status} />
            {flow.has_unpublished_changes && <UnpublishedChangesBadge />}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={translateInterfaceText("Mais ações de {value0}", { value0: (flow.name) })}
              disabled={busy}
              className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] border border-[#D9E0EA] bg-white text-[#374151] hover:bg-[#F4F6FA] disabled:opacity-50"
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 rounded-xl p-1.5">
            <DropdownMenuItem onSelect={() => onCopyCode(flow)} className="gap-2.5 rounded-lg py-2">
              <Code2 className="h-4 w-4" aria-hidden="true" />
              {translateInterfaceText("Copiar código")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onRename(flow)} className="gap-2.5 rounded-lg py-2">
              <Pencil className="h-4 w-4" aria-hidden="true" />
              {translateInterfaceText("Renomear")}</DropdownMenuItem>
            {flow.status === 'published' && (
              <DropdownMenuItem onSelect={() => onPause(flow)} className="gap-2.5 rounded-lg py-2">
                <Pause className="h-4 w-4" aria-hidden="true" />
                {translateInterfaceText("Pausar fluxo")}</DropdownMenuItem>
            )}
            {flow.status === 'paused' && (
              <DropdownMenuItem onSelect={() => onResume(flow)} className="gap-2.5 rounded-lg py-2">
                <Play className="h-4 w-4" aria-hidden="true" />
                {translateInterfaceText("Retomar fluxo")}</DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onDelete(flow)}
              className="gap-2.5 rounded-lg py-2 text-[#B91C1C] data-[highlighted]:bg-[#DC2626]"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {translateInterfaceText("Excluir fluxo e vídeos")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <p className="text-[13px] text-[#4B5563]">
        {formatVideoCount(flow.video_count)} · {formatEditedAgo(flow.updated_at)}
      </p>

      {hasMetrics ? (
        <div>
          <dl className="grid grid-cols-3 gap-2 rounded-xl bg-[#F8FAFC] p-3">
            <div className="flex flex-col gap-0.5">
              <dt className="text-[11px] text-[#6B7280]">{translateInterfaceText("Visualizações")}</dt>
              <dd className="text-[17px] font-bold text-[#111827]">{formatNumber(views)}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-[11px] text-[#6B7280]">{translateInterfaceText("Plays")}</dt>
              <dd className="text-[17px] font-bold text-[#111827]">{formatNumber(plays)}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-[11px] text-[#6B7280]">{translateInterfaceText("Taxa de play")}</dt>
              <dd className="text-[17px] font-bold text-[#111827]">
                {formatPercent(safeRate(plays, views))}
              </dd>
            </div>
          </dl>
          <p className="mt-1.5 text-[11px] text-[#6B7280]">
            {translateInterfaceText("Últimos 30 dias")}{flow.status === 'paused' && translateInterfaceText(" · o player mostra \"Conteúdo indisponível\" enquanto pausado")}
          </p>
        </div>
      ) : (
        <div className="flex min-h-[66px] items-center gap-2.5 rounded-xl bg-[#F8FAFC] p-3 text-[13px] text-[#4B5563]">
          <BarChart3 className="h-[18px] w-[18px] flex-none" aria-hidden="true" />
          {translateInterfaceText("As métricas começam quando o fluxo é publicado.")}</div>
      )}

      <div className="mt-auto flex gap-2">
        <Link to={editorPath} className={`${buttonBase} bg-[#0057FF] text-white hover:bg-[#0047D6]`}>
          {flow.status === 'draft' ? translateInterfaceText("Continuar editando") : translateInterfaceText("Editar")}
        </Link>
        {flow.status === 'published' && (
          <Link
            to={metricsPath}
            className={`${buttonBase} border border-[#D9E0EA] bg-white text-[#111827] hover:bg-[#F4F6FA]`}
          >
            {translateInterfaceText("Métricas")}</Link>
        )}
        {flow.status === 'paused' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onResume(flow)}
            className={`${buttonBase} border border-[#D9E0EA] bg-white text-[#111827] hover:bg-[#F4F6FA] disabled:opacity-50`}
          >
            {translateInterfaceText("Retomar")}</button>
        )}
      </div>
    </article>
  );
}
